
/**
 * Industry-standard random number generator with proper statistical distributions
 * Fixed critical issues: better PRNG, correct Box-Muller, proper error handling
 */
export class RandomGenerator {
  private seed: number;
  private hasSpare: boolean = false;
  private spare: number = 0;
  private static readonly MAX_SAFE_INTEGER = 2147483647; // 2^31 - 1

  constructor(seed?: number) {
    // Use proper seed initialization
    this.seed = seed ? Math.abs(Math.floor(seed)) % RandomGenerator.MAX_SAFE_INTEGER : 
                     Math.floor(Math.random() * RandomGenerator.MAX_SAFE_INTEGER);
    
    if (this.seed === 0) this.seed = 1; // Avoid zero seed
  }

  /**
   * Generate uniform random number using Park and Miller PRNG (industry standard)
   * Replaces problematic LCG with statistically validated algorithm
   */
  public uniform(): number {
    // Park-Miller PRNG: multiplier = 48271, modulus = 2^31 - 1
    const a = 48271;
    const m = RandomGenerator.MAX_SAFE_INTEGER;
    
    this.seed = (a * this.seed) % m;
    return this.seed / m;
  }

  /**
   * Fixed Box-Muller transformation with proper error handling
   */
  normal(mean: number = 0, stdDev: number = 1): number {
    // Input validation
    if (!isFinite(mean) || !isFinite(stdDev) || stdDev < 0) {
      throw new Error(`Invalid normal distribution parameters: mean=${mean}, stdDev=${stdDev}`);
    }

    if (this.hasSpare) {
      this.hasSpare = false;
      return this.spare * stdDev + mean;
    }

    this.hasSpare = true;
    
    // Generate uniform variables, avoiding edge cases
    let u = this.uniform();
    let v = this.uniform();
    
    // Avoid log(0) by ensuring u > 0
    while (u === 0) {
      u = this.uniform();
    }
    
    const mag = Math.sqrt(-2.0 * Math.log(u));
    const phase = 2.0 * Math.PI * v;
    
    this.spare = mag * Math.cos(phase);
    return mag * Math.sin(phase) * stdDev + mean;
  }

  /**
   * Generate correlated bivariate normal random variables
   */
  correlatedNormal(
    mean1: number,
    stdDev1: number,
    mean2: number,
    stdDev2: number,
    correlation: number
  ): { x: number; y: number } {
    const z1 = this.normal(0, 1);
    const z2 = this.normal(0, 1);
    
    const x = z1 * stdDev1 + mean1;
    const y = (correlation * z1 + Math.sqrt(1 - correlation * correlation) * z2) * stdDev2 + mean2;
    
    return { x, y };
  }

  /**
   * Generate multiple correlated returns using Cholesky decomposition with validation
   */
  multiVariateNormal(means: number[], covarianceMatrix: number[][]): number[] {
    // Input validation
    if (!means || !covarianceMatrix) {
      throw new Error('Missing required parameters for multivariate normal generation');
    }
    
    const n = means.length;
    if (n === 0) {
      throw new Error('Means array cannot be empty');
    }
    
    if (covarianceMatrix.length !== n) {
      throw new Error(`Covariance matrix dimension mismatch: expected ${n}x${n}, got ${covarianceMatrix.length}x${covarianceMatrix[0]?.length || 0}`);
    }
    
    // Validate means are finite
    for (let i = 0; i < n; i++) {
      if (!isFinite(means[i])) {
        throw new Error(`Invalid mean at index ${i}: ${means[i]}`);
      }
    }
    
    this.validateCorrelationMatrix(covarianceMatrix);
    
    const L = this.choleskyDecomposition(covarianceMatrix);
    const z = Array(n).fill(0).map(() => this.normal(0, 1));
    
    return means.map((mean, i) => {
      let sum = 0;
      for (let j = 0; j <= i; j++) {
        sum += L[i][j] * z[j];
      }
      return mean + sum;
    });
  }

  /**
   * Cholesky decomposition for correlation matrix with positive definiteness validation
   */
  private choleskyDecomposition(matrix: number[][]): number[][] {
    const n = matrix.length;
    
    // Validate positive definiteness using eigenvalue decomposition
    if (!this.isPositiveDefinite(matrix)) {
      console.warn('Matrix is not positive definite, applying conditioning...');
      const conditioned = this.makePositiveDefinite(matrix);
      return this.choleskyDecomposition(conditioned);
    }
    
    const L = Array(n).fill(0).map(() => Array(n).fill(0));
    
    for (let i = 0; i < n; i++) {
      for (let j = 0; j <= i; j++) {
        if (i === j) {
          let sum = 0;
          for (let k = 0; k < j; k++) {
            sum += L[j][k] * L[j][k];
          }
          const diagonal = matrix[i][i] - sum;
          if (diagonal <= 0) {
            throw new Error(`Matrix is not positive definite at position [${i},${i}]`);
          }
          L[i][j] = Math.sqrt(diagonal);
        } else {
          let sum = 0;
          for (let k = 0; k < j; k++) {
            sum += L[i][k] * L[j][k];
          }
          if (Math.abs(L[j][j]) < 1e-12) {
            throw new Error(`Near-singular matrix at position [${j},${j}]`);
          }
          L[i][j] = (matrix[i][j] - sum) / L[j][j];
        }
      }
    }
    
    return L;
  }

  /**
   * Check if matrix is positive definite using Sylvester's criterion
   */
  private isPositiveDefinite(matrix: number[][]): boolean {
    const n = matrix.length;
    
    // Check diagonal elements are positive
    for (let i = 0; i < n; i++) {
      if (matrix[i][i] <= 0) return false;
    }
    
    // Check all principal minors are positive
    for (let k = 1; k <= n; k++) {
      const subMatrix = Array(k).fill(0).map(() => Array(k).fill(0));
      for (let i = 0; i < k; i++) {
        for (let j = 0; j < k; j++) {
          subMatrix[i][j] = matrix[i][j];
        }
      }
      if (this.determinant(subMatrix) <= 0) return false;
    }
    
    return true;
  }

  /**
   * Make matrix positive definite by adding small values to diagonal
   */
  private makePositiveDefinite(matrix: number[][]): number[][] {
    const n = matrix.length;
    const result = matrix.map(row => [...row]);
    const regularization = 1e-6;
    
    // Add regularization to diagonal
    for (let i = 0; i < n; i++) {
      result[i][i] += regularization;
    }
    
    return result;
  }

  /**
   * Calculate matrix determinant
   */
  private determinant(matrix: number[][]): number {
    const n = matrix.length;
    if (n === 1) return matrix[0][0];
    if (n === 2) return matrix[0][0] * matrix[1][1] - matrix[0][1] * matrix[1][0];
    
    let det = 0;
    for (let j = 0; j < n; j++) {
      const minor = this.getMinor(matrix, 0, j);
      det += Math.pow(-1, j) * matrix[0][j] * this.determinant(minor);
    }
    return det;
  }

  /**
   * Get matrix minor by removing row i and column j
   */
  private getMinor(matrix: number[][], row: number, col: number): number[][] {
    return matrix.filter((_, i) => i !== row).map(row => row.filter((_, j) => j !== col));
  }

  /**
   * Fixed Student's t-distribution implementation
   */
  studentT(degreesOfFreedom: number): number {
    if (!isFinite(degreesOfFreedom) || degreesOfFreedom <= 0) {
      throw new Error(`Invalid degrees of freedom: ${degreesOfFreedom}`);
    }
    
    // For very high df, approximate with normal
    if (degreesOfFreedom > 100) {
      return this.normal(0, 1);
    }
    
    // Proper t-distribution: Z / sqrt(Chi²/df)
    const z = this.normal(0, 1);
    const chiSquared = this.chi2(degreesOfFreedom);
    
    // Avoid division by zero
    if (chiSquared === 0) {
      return z; // Fallback to normal
    }
    
    return z / Math.sqrt(chiSquared / degreesOfFreedom);
  }

  /**
   * Generate Chi-squared distribution random variable
   */
  private chi2(degreesOfFreedom: number): number {
    let sum = 0;
    for (let i = 0; i < degreesOfFreedom; i++) {
      const normal = this.normal(0, 1);
      sum += normal * normal;
    }
    return sum;
  }

  /**
   * Validate correlation matrix for mathematical correctness
   */
  private validateCorrelationMatrix(matrix: number[][]): void {
    const n = matrix.length;
    
    // Check dimensions
    for (let i = 0; i < n; i++) {
      if (matrix[i].length !== n) {
        throw new Error(`Correlation matrix must be square: row ${i} has ${matrix[i].length} elements, expected ${n}`);
      }
    }
    
    // Check diagonal elements are 1
    for (let i = 0; i < n; i++) {
      if (Math.abs(matrix[i][i] - 1.0) > 1e-10) {
        throw new Error(`Correlation matrix diagonal element [${i},${i}] = ${matrix[i][i]}, should be 1.0`);
      }
    }
    
    // Check symmetry
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        if (Math.abs(matrix[i][j] - matrix[j][i]) > 1e-10) {
          throw new Error(`Correlation matrix not symmetric: [${i},${j}] = ${matrix[i][j]}, [${j},${i}] = ${matrix[j][i]}`);
        }
      }
    }
    
    // Check correlation bounds
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        if (i !== j && (matrix[i][j] < -1.0 || matrix[i][j] > 1.0)) {
          throw new Error(`Invalid correlation [${i},${j}] = ${matrix[i][j]}, must be between -1 and 1`);
        }
      }
    }
  }

  /**
   * Reset the generator with new seed and proper validation
   */
  reset(seed?: number): void {
    this.seed = seed ? Math.abs(Math.floor(seed)) % RandomGenerator.MAX_SAFE_INTEGER : 
                     Math.floor(Math.random() * RandomGenerator.MAX_SAFE_INTEGER);
    
    if (this.seed === 0) this.seed = 1; // Avoid zero seed
    this.hasSpare = false;
    this.spare = 0;
  }
}
